import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { platformName } from '../platformMeta';
import type { CalendarPost } from '../types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface NeedsYouStripProps {
  posts: CalendarPost[];
  /** Turns the State filter to "Needs you". */
  onShow: () => void;
}

/**
 * The amber strip (design a1): the posts that a platform cannot post for the artist. They post
 * them by hand, then confirm. It hides when no post needs the artist.
 */
const NeedsYouStripComponent: React.FC<NeedsYouStripProps> = ({ posts, onShow }) => {
  const intl = useIntl();
  const platforms = useMemo(
    () => intl.formatList([...new Set(posts.map((post) => platformName(post.platform)))], { type: 'conjunction' }),
    [intl, posts]
  );
  if (posts.length === 0) {
    return null;
  }
  return (
    <div className={styles.attn} role="status" data-testid="calendar-needs-you">
      <span className={styles.attnCount} aria-hidden="true">
        {posts.length}
      </span>
      <span className={styles.grow}>
        <b>
          {intl.formatMessage(
            {
              id: 'app.sml.promote.calendar.needsYou.title',
              defaultMessage: '{count, plural, one {# post needs you.} other {# posts need you.}}',
            },
            { count: posts.length }
          )}
        </b>{' '}
        {intl.formatMessage(
          {
            id: 'app.sml.promote.calendar.needsYou.body',
            defaultMessage: '{platforms} cannot post them for you. Post them by hand, then confirm.',
          },
          { platforms }
        )}
      </span>
      <button type="button" className={styles.smallBtn} onClick={onShow}>
        {intl.formatMessage({ id: 'app.sml.promote.calendar.needsYou.show', defaultMessage: 'Show them' })}
      </button>
    </div>
  );
};

export const NeedsYouStrip = TypedMemo(NeedsYouStripComponent);
